import { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import { Alert, Button, Form, Input, Modal } from 'antd';
import { useHistory } from 'react-router';
import { useLocation } from 'react-router-dom';
import { SET_LOGIN_STATUS, useAuth } from '../../hooks/auth/AuthContext';
import './Login.less';
import { LoginOutlined } from '@ant-design/icons';
import useSpinner from '../../hooks/layout/useSpinner';
import { setTokenCookie } from '../../shared/helpers/set-token-cookie.helper';
import { SIGN_IN } from './Login.gql';
import leftShadow from '../../assets/images/img-shadow-left.png';
import rightShadow from '../../assets/images/img-shadow-right.png';
import { FormattedMessage, useIntl } from 'react-intl';
import { encode } from 'base-64';
import Register from '../register/Register';

const Login = () => {
  const intl = useIntl();
  const { authState, authDispatch } = useAuth();
  const history = useHistory();
  const location: any = useLocation();
  const { setLoading } = useSpinner();
  const { from } = location.state || { from: { pathname: '/' } };
  const [signIn, { data, loading, error: signInError }] = useMutation(SIGN_IN, {
    fetchPolicy: "no-cache",
    errorPolicy: "all",
  });
  const [errorMessage, setErrorMessage] = useState('');
  const [registerModalVisible, setRegisterModalVisible] = useState(false);

  useEffect(() => {
    if (authState.isLogin) {
      history.replace(from);
    }
  }, [authState.isLogin]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setLoading(loading); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  useEffect(() => {
    if (data && data.signIn) {
      const { a, r, sid } = data.signIn;
      setTokenCookie(process.env.REACT_APP_ACCESS_TOKEN || "gmf-at", a);
      setTokenCookie(process.env.REACT_APP_REFRESH_TOKEN || "gmf-rt", r);
      setTokenCookie("gmf-sid", sid);
      authDispatch({ type: SET_LOGIN_STATUS, payload: true });
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (signInError) {
      switch (signInError.message) {
        case 'INVALID_LOGIN':
          setErrorMessage('Invalid username / password!');
          break;
        case 'INVALID_OTP':
          setErrorMessage('Invalid OTP. Please try again!');
          break;
        case 'REQUIRED_OTP':
          setErrorMessage('Please key in OTP from google authenticator!');
          break;
        case 'ACCOUNT_LOCKED':
          setErrorMessage('Your account is locked, please contact system administrator!');
          break;
        default:
          setErrorMessage('Login Failed!');
          break;
      }
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signInError]);

  const onFinish = (values: any) => {
    signIn({
      variables: {
        userName: values.username,
        password: encode(values.password),
        companyId: process.env.REACT_APP_COMPANY_ID ?? ''
      }
    });

    //authDispatch({ type: SET_LOGIN_STATUS, payload: true });
  };

  const onFinishFailed = (errorInfo: any) => {
  };

  return (
    <>
      <div className="login-fullpage-container">
        <div className="app-form-small-container">
          <div className="header-band">
            <h2 className="header-2"><FormattedMessage id='PORTAL_NAME' /></h2>
            <img className="left-shadow-img" src={leftShadow} alt="left-shadow" />
            <img className="right-shadow-img" src={rightShadow} alt="right-shadow" />
          </div>
          {errorMessage && <Alert message={errorMessage} type="error" />}
          <Form className="login-form"
            layout="vertical"
            initialValues={{ remember: true }}
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
          >
            <Form.Item
              label={intl.formatMessage({ id: 'USERNAME' })}
              name="username"
              rules={[{ required: true, message: intl.formatMessage({ id: 'LOGIN_USERNAME_REQUIRED' }) }]}
            >
              <Input placeholder={intl.formatMessage({ id: 'USERNAME' })} maxLength={255} />
            </Form.Item>

            <Form.Item
              label={intl.formatMessage({ id: 'PASSWORD' })}
              name="password"
              rules={[
                { required: true, message: intl.formatMessage({ id: 'PASSWORD_REQUIRED' }) },
                { min: 8, message: intl.formatMessage({ id: 'PASSWORD_LENGTH' }) },
                {
                  pattern: /[A-Za-z0-9]{6,19}$/,
                  message: intl.formatMessage({ id: 'LOGIN_PASSWORD_FORMAT' })
                }]}
            >
              <Input.Password placeholder={intl.formatMessage({ id: 'PASSWORD' })} />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit">
                <LoginOutlined /> <FormattedMessage id='LOGIN' />
              </Button>
            </Form.Item>
          </Form>
          <div className='auth-bottom-btns'>
            <div className='auth-register' onClick={() => { setRegisterModalVisible(true) }}><FormattedMessage id='REGISTER' /></div>
          </div>
        </div>
      </div>
      <Modal
        title={<FormattedMessage id='REGISTER' />}
        onCancel={() => { setRegisterModalVisible(false) }}
        open={registerModalVisible}
        footer={null}
      >
        <Register from={from} />
      </Modal>
    </>
  );
}

export default Login;
